/**
 * Le pilotage : la rentabilité, par chantier et par salarié, et le carnet de
 * commandes.
 *
 * Trois lectures seulement, et c'est délibéré. Le tableau de bord de pilotage
 * affiche bien davantage — trésorerie, encours, marge du mois, charge des
 * semaines à venir — mais tout cela se lit déjà ailleurs, par des services qui
 * existent et que d'autres écrans utilisent. En refaire une copie ici
 * garantirait qu'un même montant finisse par différer d'un écran à l'autre,
 * ce qui est exactement le défaut que ce dépôt passe son temps à réparer.
 *
 * Ne vit donc ici que ce qui n'existait nulle part :
 *
 *   · la **marge devisée** d'un chantier, comparée à la marge constatée. Les
 *     deux chiffres existaient, jamais côte à côte ;
 *   · les **heures pointées** par chantier, que seul le registre connaissait ;
 *   · le **carnet de commandes** — ce qui est signé et pas encore commencé ;
 *   · **ce que chaque salarié coûte et rapporte**, qui n'est pas un chiffre de
 *     plus mais la marge des chantiers répartie entre ceux qui les ont faits.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { Backlog, ChantierProfitability, EmployeeProfitability } from "./types.js";

// ─── Entrées ───────────────────────────────────────────────────────────────

export const readProfitabilityInput = z.object({
  confirmationId: z.string().optional(),
});

export const readBacklogInput = z.object({
  confirmationId: z.string().optional(),
});

export const readEmployeeProfitabilityInput = z.object({
  confirmationId: z.string().optional(),
});

export type ReadProfitabilityInput = z.infer<typeof readProfitabilityInput>;
export type ReadBacklogInput = z.infer<typeof readBacklogInput>;
export type ReadEmployeeProfitabilityInput = z.infer<typeof readEmployeeProfitabilityInput>;

// ─── Port ──────────────────────────────────────────────────────────────────

export interface PilotageRepository {
  /**
   * Une ligne par chantier ouvert : avancement, heures pointées, marge
   * devisée, marge constatée.
   */
  chantierProfitability(ctx: ExecutionContext): Promise<ChantierProfitability[]>;
  /** Devis acceptés dont le chantier n'a pas encore démarré. */
  backlog(ctx: ExecutionContext): Promise<Backlog>;

  /**
   * Ce que chaque salarié coûte et rapporte.
   *
   * Le dépôt assemble, il ne décide pas : la répartition vit dans
   * `computeEmployeeProfitability`, qui est pure et éprouvable sans base.
   */
  employeeProfitability(ctx: ExecutionContext): Promise<EmployeeProfitability[]>;
}

// ─── Services ──────────────────────────────────────────────────────────────

export function createPilotageServices(repo: PilotageRepository) {
  const lireRentabilite = defineService<ReadProfitabilityInput, ChantierProfitability[]>({
    name: "lire_rentabilite_chantiers",
    level: ActionLevel.Read,
    handler: (ctx) => repo.chantierProfitability(ctx),
  });

  const lireCarnet = defineService<ReadBacklogInput, Backlog>({
    name: "lire_carnet_commandes",
    level: ActionLevel.Read,
    handler: (ctx) => repo.backlog(ctx),
  });

  /**
   * Réservé au patron, et déclaré ici plutôt que laissé à la base.
   *
   * `employee_costs` est déjà fermée par la policy `patron_only` : un
   * conducteur qui appellerait ce service ne recevrait que des taux `null`. Le
   * résultat serait pire qu'un refus — un tableau où chaque salarié coûte 0 €
   * et rapporte donc l'intégralité de la marge. Une donnée absente qui se lit
   * comme une donnée nulle est exactement le défaut que ce dépôt a corrigé sur
   * le coût horaire de la fiche salarié.
   */
  const lireRentabiliteSalaries = defineService<
    ReadEmployeeProfitabilityInput,
    EmployeeProfitability[]
  >({
    name: "lire_rentabilite_salaries",
    level: ActionLevel.Read,
    allowedRoles: ["patron"],
    handler: (ctx) => repo.employeeProfitability(ctx),
  });

  return { lireRentabilite, lireCarnet, lireRentabiliteSalaries };
}

export type PilotageServices = ReturnType<typeof createPilotageServices>;
